import React from 'react';

import styled from '@emotion/styled';

import { COLORS, mq } from '@clutter/clean';

import { FunnelEntryPoint } from '@shared/funnel_entry_point';
import { FunnelEntryPointForm } from '@shared/funnel_entry_point_form';
import { FunnelEntryPointErrorMessage } from '@shared/funnel_entry_point_message';
import { FunnelEntryPointSubmitButton } from '@shared/funnel_entry_point_submit_button';
import { FunnelEntryPointZipInput } from '@shared/funnel_entry_point_zip_input';
import { ServiceSelection } from '@shared/service_selector/constants';

import { ServiceAreaMap } from './service_area_map';
import { Arrow } from '@images/svg/arrow';

const Container = styled.section`
  background: ${COLORS.cloud};
  position: relative;
  ${mq({
    padding: ['60px 24px', '80px 60px', '85px 80px'],
  })}
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--M, 24px);
  ${mq({
    width: ['auto', 'auto', '572px'],
    marginLeft: ['16px', '24px', 'auto'],
    marginRight: ['16px', '24px', 'auto'],
  })}

  & > form {
    align-self: stretch;
  }
`;

const Title = styled.h2`
  color: var(--Text-Panther, #000);
  text-align: center;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-family: Recoleta;
  font-style: normal;
  font-weight: 500;
  line-height: 140%;
  ${mq({
    width: ['100%', '100%', '572px'],
    fontSize: ['28px !important', '32px !important', '40px !important'],
    height: ['auto', 'auto', '56px'],
  })}
`;

const FormWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ZipInputContainer = styled.div`
  display: flex;
  align-items: center;
  height: 56px;
  border-radius: 999px;
  background: ${COLORS.grayBackground};
  ${mq({
    width: ['100%', '100%', '572px'],
    padding: ['8px 24px', '4px', '4px'],
    gap: ['18px', '4px', '4px'],
  })}
`;

const ZipInputWrapper = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  align-items: center;
  border-radius: 999px;
  background: ${COLORS.grayBackground};
  ${mq({
    padding: ['0', '0 20px', '0 20px'],
  })}

  input {
    width: 100%;
    height: 100%;
    border: none !important;
    background: transparent;
    outline: none !important;
    color: var(--Typography-Hippo, #757575);
    font-feature-settings:
      'liga' off,
      'clig' off;
    font-family: 'Basier Square';
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 27px;
    box-shadow: none !important;

    &:focus-visible {
      outline: 2px solid ${COLORS.tealPrimary};
      outline-offset: 2px;
    }
  }

  input::placeholder {
    color: #6e6e6e;
    opacity: 1;
  }

  /* Autofill fixes to keep input matching the gray container */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: var(--Typography-Hippo, #757575) !important;
    -webkit-box-shadow: 0 0 0px 1000px ${COLORS.grayBackground} inset !important;
    box-shadow: 0 0 0px 1000px ${COLORS.grayBackground} inset !important;
    background-color: ${COLORS.grayBackground} !important;
    caret-color: var(--Typography-Hippo, #757575) !important;
    border-radius: 999px !important;
    -webkit-background-clip: padding-box;
    background-clip: padding-box;
    transition: background-color 99999s ease-in-out 0s;
  }

  input:autofill,
  input:autofill:hover,
  input:autofill:focus {
    -webkit-text-fill-color: var(--Typography-Hippo, #757575) !important;
    -webkit-box-shadow: 0 0 0px 1000px ${COLORS.grayBackground} inset !important;
    box-shadow: 0 0 0px 1000px ${COLORS.grayBackground} inset !important;
    background-color: ${COLORS.grayBackground} !important;
    caret-color: var(--Typography-Hippo, #757575) !important;
    border-radius: 999px !important;
    -webkit-background-clip: padding-box;
    background-clip: padding-box;
    transition: background-color 99999s ease-in-out 0s;
  }
`;

const SubmitButtonWrapper = styled.div`
  flex-shrink: 0;

  button {
    border-radius: 50% !important;
    padding: 0 !important;
    width: 34px !important;
    height: 34px !important;
    min-width: 34px !important;
    background: ${COLORS.tealPrimary} !important;
    border: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    cursor: pointer;
    transition: background-color 0.2s ease;
    box-shadow: none !important;

    @media (min-width: 600px) {
      width: 48px !important;
      height: 48px !important;
      min-width: 48px !important;
    }

    &:hover {
      background: ${COLORS.tealDark} !important;
    }

    &:focus-visible {
      outline: 2px solid ${COLORS.tealPrimary};
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    & > * {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    svg {
      width: 18px;

      @media (min-width: 600px) {
        width: 20px;
      }
    }
  }
`;

const ErrorMessage = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${COLORS.flower};
  padding: 10px 16px;
  font-size: 14px;
  color: ${COLORS.toucan};
  border-radius: 4px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${COLORS.flower};
  }
`;

const MapContainer = styled.div`
  margin-top: 40px;
  border-radius: var(--S, 18px);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.18);
  ${mq({
    marginLeft: ['16px', '24px', '159px'],
    marginRight: ['16px', '24px', '155px'],
  })}
`;

interface ServiceAreaInquiryProps {
  states: ReadonlyArray<
    Pick<import('@graphql/platform').Location__State, 'id' | 'slug'> & {
      cities: ReadonlyArray<
        Pick<import('@graphql/platform').Location__City, 'id' | 'name'> & {
          center: import('@graphql/platform').LatLngFragment;
        }
      >;
    }
  >;
}

export const ServiceAreaInquiry: React.FC<ServiceAreaInquiryProps> = ({
  states,
}) => {
  return (
    <Container id="service-area">
      <Inner>
        <Title>Do you serve my area?</Title>
        <FunnelEntryPoint
          service={ServiceSelection.Storage}
          wtConfig={{ container: 'service_area_inquiry' }}
        >
          <FunnelEntryPointForm id="service_area_form">
            <FormWrapper>
              <ZipInputContainer>
                <ZipInputWrapper>
                  <FunnelEntryPointZipInput shortenPlaceholderText />
                </ZipInputWrapper>
                <SubmitButtonWrapper>
                  <FunnelEntryPointSubmitButton>
                    <Arrow fillColor={COLORS.cloud} direction="right" />
                  </FunnelEntryPointSubmitButton>
                </SubmitButtonWrapper>
              </ZipInputContainer>
              <FunnelEntryPointErrorMessage>
                <ErrorMessage />
              </FunnelEntryPointErrorMessage>
            </FormWrapper>
          </FunnelEntryPointForm>
        </FunnelEntryPoint>
      </Inner>
      <MapContainer>
        <ServiceAreaMap states={states} height={['300px', null, '600px']} />
      </MapContainer>
    </Container>
  );
};
